import { connect } from 'react-redux'
import { createBrowserHistory, createMemoryHistory } from 'history'
import pathToRegexp, { compile } from 'path-to-regexp'
import { matchPath } from 'react-router'
import { takeEvery, put, call, select } from 'redux-saga/effects'
import { createSelector } from 'reselect'

import URI from 'urijs'
import { map, filter, omit, omitBy, get, head, values, startCase, has, isNil, isEmpty } from 'lodash'

import { utils } from 'parking-react-common'
import { ui, auth, api } from '../redux'

//
//  ROUTES

const authMethod = utils.featureFlag('authMethod')

const components = {
    sms: {
        login          : 'PhoneLoginContainer',
        passwordChange : 'PhonePasswordChangeContainer'
    },
    email: {
        login          : 'EmailLoginContainer',
        passwordChange : 'EmailPasswordChangeContainer'
    }
}


export const ROUTES = {
    login: {
        path      : '/login',
        component : components[authMethod].login,
        isPublic  : true
    },
    phoneLoginWithPassword: {
        path      : '/login-with-password',
        component : 'PhoneLoginWithPasswordContainer',
        isPublic  : true
    },
    emailSignup: {
        path      : '/signup',
        component : 'EmailSignupContainer',
        isPublic  : true
    },
    emailVerification: {
        path      : '/account-activation/:token',
        component : 'EmailVerificationContainer',
        isPublic  : true
    },
    emailPasswordReset: {
        path      : '/password-reset',
        component : 'EmailPasswordResetContainer',
        isPublic  : true
    },
    emailPasswordConfirmation: {
        path      : '/password-confirmation/:uid/:code',
        component : 'EmailPasswordConfirmationContainer',
        isPublic  : true
    },
    accountSettings: {
        path      : '/account-settings',
        component : 'AccountSettingsContainer'
    },
    preferences: {
        path      : '/preferences',
        component : 'PreferencesContainer'
    },
    dashboard: {
        path      : '/',
        component : 'MainContainer'
    },
    parkingLots: {
        path      : '/parking-lots/:id(\\d+)?/:tab?/:action?',
        component : 'ParkingLotsContainer'
    },
    payments: {
        path      : '/billing/payments',
        component : 'PaymentsContainer'
    },
    invoices: {
        path      : '/billing/invoices',
        component : 'InvoicesContainer'
    },
    topUps: {
        path      : '/billing/top-ups',
        component : 'TopUpsContainer'
    },
    charts: {
        path      : '/billing/charts',
        component : 'ChartsContainer'
    },
    users: {
        path      : '/users/accounts/:id(\\d+)?/:action?',
        component : 'UsersContainer'
    },
    invitations: {
        path      : '/users/invitations',
        component : 'InvitationsContainer'
    },
    sessions: {
        path      : '/sessions',
        component : 'SessionsContainer'
    },
    externalSessions: {
        path      : '/external-sessions',
        component : 'ExternalSessionsContainer'
    },
    interrogations: {
        path      : '/interrogations',
        component : 'InterrogationsContainer'
    },
    passwordChange: {
        path      : '/password-change',
        component : components[authMethod].passwordChange
    }
}

//
//  ACTIONS

export const ROUTE_CHANGED = '@ routing / ROUTE_CHANGED'

export const updateRoute = (route) => ({ type: ROUTE_CHANGED, payload: route })


//
//  REDUCER

export function reducer(state = {}, action = {}) {
    switch (action.type) {
        case ROUTE_CHANGED: {
            const { pathname, search } = action.payload
            const matchedRoutes = filter(
                map(ROUTES, ({ path }, key) => ({ key, ...matchPath(pathname, { path, exact: true }) })),
                'isExact'
            )

            const matchedRoute = head(matchedRoutes)
            const routeParams = URI.parseQuery(search) || {}

            if (!isEmpty(matchedRoute)) {
                return {
                    ...matchedRoute,
                    params: {
                        ...matchedRoute.params,
                        ...routeParams
                    }
                }
            }

            return {}
        }
        default: return state
    }
}


//
//  SAGA

export function* saga() {
    yield takeEvery(ROUTE_CHANGED, closeDropdownMenus)
    yield takeEvery(auth.EMAIL_PASSWORD_CONFIRMATION_SUCCEEDED, redirectToLogin)
    yield takeEvery([api.parkingLots.CREATE_SUCCEEDED, api.parkingLots.UPDATE_SUCCEEDED], redirectToViewMode)
}


function* closeDropdownMenus() {
    yield put(ui.closeMenus(ui.MENUS.dropdown))
}

function* redirectToLogin() {
    yield call(push, routeFor('login'))
}

function* redirectToViewMode({ payload }) {
    const parkingLot = head(values(payload.data))
    const params = yield select(getParams)

    yield call(push, routeFor('parkingLots', {
        id     : get(parkingLot, 'id'),
        tab    : params.tab === 'add' ? 'basic-info' : get(params, 'tab', 'basic-info'),
        action : null
    }))
}

//
//   HELPERS and UTILITIES

export const history = process.env.NODE_ENV === 'test' ? createMemoryHistory() : createBrowserHistory()
const { push, replace } = history
export { push, replace }

export function routeFor(key, params = {}) {
    if (!has(ROUTES, key)) {
        throw new Error(`Invalid route key: ${key}`)
    }

    const route = ROUTES[key].path
    const url = new URI({ path: compile(route)(params) })
    const keys = []

    pathToRegexp(route, keys)
    url.query(omitBy(omit(params, map(keys, 'name')), isNil))

    return url.toString()
}

export const withRouter = (component) => connect((state) => state.routing)(component)

//
//  SELECTORS

export const getCurrentRoute = (state) => state.routing
export const getParams = createSelector(
    getCurrentRoute,
    (route) => route.params
)
export const getPageTitle = createSelector(
    getCurrentRoute,
    (route) => startCase(route.key)
)
